import { useConfirmation } from "shared/components/ConfirmationProvider";

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  alpha,
  Chip,
  Paper,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  Edit,
  WhatsApp,
  CheckCircle,
  Error as ErrorIcon,
  Save,
  Close,
  Refresh,
  Lock,
  Group,
  Work,
  Computer,
} from "@mui/icons-material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import PropTypes from "prop-types";
import { getCommerceWhatsAppTerminalsService } from "modules/jarvis/services/whatsappService";

const EditWhatsappCopDialog = ({
  open,
  onClose,
  onUpdate,
  configuracion,
  attributes,
  loading = false,
}) => {
  const confirm = useConfirmation();

  const [formData, setFormData] = useState({
    idcommerce: "",
    idterminal: "",
    namecommerce: "",
    terminalname: "",
    groupnoc: "",
    groupName: "",
    chatId: "",
    nameworkflows: "",
    status: true,
    pulse: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [terminales, setTerminales] = useState([]);
  const [loadingTerminales, setLoadingTerminales] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [responseData, setResponseData] = useState(null);

  // Determinar si el workflow seleccionado es de INTEGRACION
  const isJarvisIntegration =
    formData.nameworkflows &&
    (formData.nameworkflows.includes("INTEGRACION") ||
      formData.nameworkflows.includes("INTEGRACIÓN"));

  // Refs para controlar efectos
  const isInitialMount = useRef(true);
  const prevConfiguracionRef = useRef(null);

  // Cargar terminales cuando se abre el diálogo y cambia el comercio
  useEffect(() => {
    let isMounted = true;

    const cargarTerminales = async () => {
      if (configuracion && configuracion.idCommerce && open) {
        setLoadingTerminales(true);
        try {
          const result = await getCommerceWhatsAppTerminalsService(configuracion.idCommerce);
          if (result.success && isMounted) {
            setTerminales(result.data);
          } else if (isMounted) {
            console.error("Error al cargar terminales:", result.error);
          }
        } catch (error) {
          if (isMounted) {
            console.error("Error al cargar terminales:", error);
          }
        } finally {
          if (isMounted) {
            setLoadingTerminales(false);
          }
        }
      } else if (isMounted) {
        setTerminales([]);
        setSelectedTerminal(null);
      }
    };

    if (open && configuracion) {
      cargarTerminales();
    }

    return () => {
      isMounted = false;
    };
  }, [configuracion?.idCommerce, open]);

  // Cargar datos de la configuración cuando se abre el diálogo o cambia la configuración
  useEffect(() => {
    if (configuracion && open) {
      // Verificar si realmente cambió la configuración para evitar ciclos
      if (prevConfiguracionRef.current !== configuracion) {
        prevConfiguracionRef.current = configuracion;

        // Buscar la terminal seleccionada en la lista de terminales
        const terminalActual = terminales.find((t) => t.id === configuracion.idTerminal);

        setFormData({
          idcommerce: configuracion.idCommerce?.toString() || "",
          idterminal: configuracion.idTerminal?.toString() || "",
          namecommerce: configuracion.nombreComercio || "",
          terminalname: configuracion.terminalName || "",
          groupnoc: configuracion.grupoAlertaNOC || "",
          groupName: configuracion.grupoComercio || "",
          chatId: configuracion.chatId || "",
          nameworkflows: configuracion.workflow || "",
          status: configuracion.activo === true,
          pulse: configuracion.pulso === true,
        });

        setSelectedTerminal(terminalActual || null);
      }
    }
  }, [configuracion, open, terminales]);

  // Resetear formulario cuando se cierra
  useEffect(() => {
    if (!open) {
      resetForm();
      isInitialMount.current = true;
      prevConfiguracionRef.current = null;
    }
  }, [open]);

  const resetForm = () => {
    setFormData({
      idcommerce: "",
      idterminal: "",
      namecommerce: "",
      terminalname: "",
      groupnoc: "",
      groupName: "",
      chatId: "",
      nameworkflows: "",
      status: true,
      pulse: false,
    });
    setErrors({});
    setTouched({});
    setTerminales([]);
    setSelectedTerminal(null);
    setIsSubmitting(false);
    setSubmitStatus(null);
    setStatusMessage("");
    setResponseData(null);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    validateField(field, value);
  };

  const handleSwitchChange = (field) => (event) => {
    const checked = event.target.checked;

    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleTerminalSelect = (event) => {
    const terminalId = event.target.value;

    if (terminalId === "") {
      // Opción "Sin terminal" seleccionada
      setFormData((prev) => ({
        ...prev,
        idterminal: "",
        terminalname: "",
      }));
      setSelectedTerminal(null);
    } else {
      const terminalSeleccionada = terminales.find((t) => t.id.toString() === terminalId);
      setFormData((prev) => ({
        ...prev,
        idterminal: terminalId,
        terminalname: terminalSeleccionada?.nombre || "",
      }));
      setSelectedTerminal(terminalSeleccionada || null);
    }

    validateField("idterminal", terminalId);
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "idterminal":
        // Solo validar terminal si NO es J.A.R.V.I.S INTEGRACION
        if (!isJarvisIntegration && !value) error = "Debe seleccionar una terminal";
        break;
      case "groupnoc":
        if (!value) error = "El grupo NOC es requerido";
        break;
      case "nameworkflows":
        if (!value) error = "El workflow es requerido";
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateForm = () => {
    const fieldsToValidate = ["groupnoc", "nameworkflows"];

    // Agregar idterminal a la validación solo si NO es J.A.R.V.I.S INTEGRACION
    if (!isJarvisIntegration) {
      fieldsToValidate.push("idterminal");
    }

    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) isValid = false;
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    });

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitStatus("error");
      setStatusMessage("Por favor, complete todos los campos requeridos correctamente.");
      return;
    }

    // Preparar datos para enviar - MISMO PAYLOAD QUE EN CREATE
    const datosAEnviar = {
      status: formData.status === true,
      idcommerce: parseInt(formData.idcommerce, 10),
      // Si es J.A.R.V.I.S INTEGRACION y no hay terminal, enviar null
      idterminal:
        isJarvisIntegration && !formData.idterminal ? null : parseInt(formData.idterminal, 10),
      terminalname: formData.terminalname || "",
      namecommerce: formData.namecommerce,
      groupnoc: formData.groupnoc,
      groupName: formData.groupName || "",
      chatId: formData.chatId || "",
      nameworkflows: formData.nameworkflows,
      pulse: formData.pulse === true,
    };

    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage("Actualizando configuración...");
    setResponseData(null);

    try {
      const result = await onUpdate(datosAEnviar);

      if (result && result.success) {
        setResponseData(result.data);
        setStatusMessage(result.message || "Configuración actualizada exitosamente.");
        setSubmitStatus("success");

        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(result?.error || "Error al actualizar la configuración");
      }
    } catch (error) {
      console.error("Error al actualizar configuración:", error);
      setSubmitStatus("error");
      setStatusMessage(error.message || "Error desconocido al actualizar la configuración.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (isSubmitting) {
      if (await confirm("La actualización está en progreso. ¿Desea cancelar?")) {
        setIsSubmitting(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const renderForm = () => (
    <Stack spacing={3}>
      {/* Información del comercio (solo visual) */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "info.main",
          backgroundColor: alpha("#2196f3", 0.03),
        }}
      >
        <MDTypography
          variant="subtitle1"
          color="info"
          fontWeight="600"
          mb={2.5}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Lock fontSize="small" />
          INFORMACIÓN DEL COMERCIO (SOLO VISUAL)
        </MDTypography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID Comercio"
              value={formData.idcommerce}
              disabled
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsApp sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre Comercio"
              value={formData.namecommerce}
              disabled
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Computer sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Formulario de edición */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Grid container spacing={3}>
          {/* Grupo NOC - EDITABLE */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("groupnoc")}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.95rem",
                },
              }}
            >
              <InputLabel sx={{ fontSize: "0.95rem" }}>Grupo NOC *</InputLabel>
              <Select
                value={formData.groupnoc}
                onChange={handleChange("groupnoc")}
                label="Grupo NOC *"
                disabled={isSubmitting}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Group sx={{ color: "#075e54" }} />
                  </InputAdornment>
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: "0.95rem", fontWeight: 500 }}>
                      {selected}
                    </Typography>
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      "& .MuiMenuItem-root": {
                        py: 1.5,
                        px: 2,
                        fontSize: "0.95rem",
                      },
                    },
                  },
                }}
              >
                {attributes?.groupnoc?.map((grupo) => (
                  <MenuItem key={grupo} value={grupo}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: getGrupoColor(grupo),
                        }}
                      />
                      <Typography variant="body1" sx={{ fontSize: "0.95rem" }}>
                        {grupo}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("groupnoc") && (
                <MDTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.groupnoc}
                </MDTypography>
              )}
            </FormControl>
          </Grid>

          {/* Workflow - EDITABLE (AHORA PRIMERO ANTES DE TERMINAL) */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("nameworkflows")}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.95rem",
                },
              }}
            >
              <InputLabel sx={{ fontSize: "0.95rem" }}>Workflow *</InputLabel>
              <Select
                value={formData.nameworkflows}
                onChange={handleChange("nameworkflows")}
                label="Workflow *"
                disabled={isSubmitting}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Work sx={{ color: "#075e54" }} />
                  </InputAdornment>
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: "0.95rem", fontWeight: 500 }}>
                      {selected}
                    </Typography>
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      "& .MuiMenuItem-root": {
                        py: 1.5,
                        px: 2,
                        fontSize: "0.95rem",
                      },
                    },
                  },
                }}
              >
                {attributes?.workflows?.map((workflow) => (
                  <MenuItem key={workflow.nameworkflows} value={workflow.nameworkflows}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Work sx={{ color: "#075e54" }} />
                      <Typography variant="body1" sx={{ fontSize: "0.95rem" }}>
                        {workflow.nameworkflows}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("nameworkflows") && (
                <MDTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.nameworkflows}
                </MDTypography>
              )}
            </FormControl>
          </Grid>

          {/* Terminal - Selector EDITABLE con opción "Sin terminal" para INTEGRACION (AHORA DESPUÉS DE WORKFLOW) */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("idterminal")}
              disabled={loadingTerminales}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
              }}
            >
              <InputLabel error={isFieldError("idterminal")}>
                Terminal {isJarvisIntegration ? "(Opcional)" : "*"}
              </InputLabel>
              <Select
                value={formData.idterminal}
                onChange={handleTerminalSelect}
                label={`Terminal ${isJarvisIntegration ? "(Opcional)" : "*"}`}
                error={isFieldError("idterminal")}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Computer sx={{ color: isFieldError("idterminal") ? "#f44336" : "#075e54" }} />
                  </InputAdornment>
                }
                renderValue={(selected) => {
                  if (!selected) return <em>Sin terminal</em>;
                  const terminal = terminales.find((t) => t.id.toString() === selected);
                  return terminal?.nombre || "Seleccione una terminal";
                }}
              >
                {/* Solo mostrar "Sin terminal" para workflows de INTEGRACION */}
                {isJarvisIntegration && (
                  <MenuItem value="">
                    <em>Sin terminal (opcional)</em>
                  </MenuItem>
                )}
                {terminales.map((terminal) => (
                  <MenuItem key={terminal.id} value={terminal.id.toString()}>
                    {terminal.nombre}
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("idterminal") && (
                <MDTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.idterminal}
                </MDTypography>
              )}
              {isJarvisIntegration && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Para workflows de INTEGRACION la terminal es opcional
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Terminal Name - Solo lectura (auto-completado) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Terminal Name"
              value={formData.terminalname}
              disabled
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Computer sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Grupo Nombre - SOLO LECTURA */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Grupo Nombre"
              value={formData.groupName}
              disabled
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Group sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Chat ID - SOLO LECTURA */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Chat ID"
              value={formData.chatId}
              disabled
              variant="outlined"
              placeholder="No disponible"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsApp sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Switches - EDITABLES */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: alpha("#075e54", 0.02),
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status}
                        onChange={handleSwitchChange("status")}
                        color="success"
                        disabled={isSubmitting}
                        size="medium"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ fontSize: "0.95rem" }}>
                          Activo
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {formData.status ? "Activado" : "Desactivado"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="end"
                    sx={{
                      margin: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        flex: 1,
                      },
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: alpha("#075e54", 0.02),
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.pulse}
                        onChange={handleSwitchChange("pulse")}
                        color="info"
                        disabled={isSubmitting}
                        size="medium"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ fontSize: "0.95rem" }}>
                          Pulso
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {formData.pulse ? "Activo" : "Inactivo"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="end"
                    sx={{
                      margin: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        flex: 1,
                      },
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Resumen de cambios */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: "1px dashed",
          borderColor: alpha("#075e54", 0.3),
          backgroundColor: alpha("#075e54", 0.02),
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          fontWeight="600"
          gutterBottom
          sx={{ fontSize: "0.9rem", mb: 1.5 }}
        >
          RESUMEN DE CAMBIOS
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Terminal:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.terminalname || (isJarvisIntegration ? "Opcional" : "Sin terminal")}
            </Typography>
          </Grid>
          <Grid item xs={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Grupo NOC:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.groupnoc || "—"}
            </Typography>
          </Grid>
          <Grid item xs={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Workflow:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.nameworkflows || "—"}
            </Typography>
          </Grid>
          <Grid item xs={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Estado:
            </Typography>
            <Chip
              label={formData.status ? "ACTIVO" : "INACTIVO"}
              size="small"
              color={formData.status ? "success" : "error"}
              sx={{ height: "24px", fontSize: "0.7rem", fontWeight: 600 }}
            />
          </Grid>
          <Grid item xs={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Pulso:
            </Typography>
            <Chip
              label={formData.pulse ? "ACTIVO" : "INACTIVO"}
              size="small"
              color={formData.pulse ? "info" : "default"}
              sx={{ height: "24px", fontSize: "0.7rem", fontWeight: 600 }}
            />
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );

  // Función para obtener color según el grupo NOC
  const getGrupoColor = (grupo) => {
    const grupoLower = grupo?.toLowerCase() || "";
    if (grupoLower.includes("terminal")) return "#5c6bc0";
    if (grupoLower.includes("centro comercial")) return "#66bb6a";
    if (grupoLower.includes("tequendama")) return "#ffa726";
    if (grupoLower.includes("integracion")) return "#42a5f5";
    return "#78909c";
  };

  const renderSuccessContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 3 }} />
      <MDTypography variant="h6" fontWeight="bold" color="success" gutterBottom>
        Configuración Actualizada Exitosamente
      </MDTypography>
      <MDTypography variant="body2" color="text.secondary" paragraph>
        {statusMessage}
      </MDTypography>
    </Box>
  );

  const renderErrorContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 3 }} />
      <MDTypography variant="h6" fontWeight="bold" color="error" gutterBottom>
        Error al Actualizar Configuración
      </MDTypography>
      <Alert severity="error" sx={{ mb: 3 }}>
        <MDTypography variant="body2">{statusMessage}</MDTypography>
      </Alert>
    </Box>
  );

  const renderLoadingContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CircularProgress size={80} thickness={4} sx={{ color: "#075e54", mb: 3 }} />
      <MDTypography variant="h6" fontWeight="medium" color="#075e54" gutterBottom>
        Actualizando Configuración
      </MDTypography>
      <MDTypography variant="caption" color="text.secondary" display="block">
        {statusMessage}
      </MDTypography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "#075e54" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : (
            <Edit sx={{ mr: 2, fontSize: "1.75rem", color: "#075e54" }} />
          )}
          <Box>
            <MDTypography variant="h5" fontWeight="bold">
              Editar Configuración WhatsApp
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              {configuracion
                ? `Editando: ${configuracion.nombreComercio}`
                : "Seleccione una configuración"}
            </MDTypography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Stack spacing={3}>
          {(isSubmitting || submitStatus) && submitStatus !== "success" && (
            <Alert
              severity={isSubmitting ? "info" : submitStatus === "success" ? "success" : "error"}
              icon={isSubmitting ? <CircularProgress size={20} /> : undefined}
              sx={{ borderRadius: "8px" }}
            >
              <MDTypography variant="body2" fontWeight="medium">
                {statusMessage}
              </MDTypography>
            </Alert>
          )}

          {isSubmitting && renderLoadingContent()}
          {submitStatus === "success" && renderSuccessContent()}
          {submitStatus === "error" && renderErrorContent()}
          {!isSubmitting && !submitStatus && renderForm()}
        </Stack>
      </DialogContent>

      {/* Footer */}
      {!isSubmitting && !submitStatus && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{
                px: 3,
                minWidth: 120,
                borderRadius: "8px",
                height: "48px",
              }}
            >
              Cancelar
            </MDButton>
            <MDButton
              onClick={handleSubmit}
              variant="contained"
              sx={{
                px: 4,
                minWidth: 200,
                borderRadius: "8px",
                fontWeight: "bold",
                height: "48px",
                backgroundColor: "#075e54",
                "&:hover": {
                  backgroundColor: "#128c7e",
                },
              }}
              startIcon={<Save />}
              disabled={
                isSubmitting ||
                !formData.groupnoc ||
                !formData.nameworkflows ||
                (!isJarvisIntegration && !formData.idterminal)
              }
            >
              ACTUALIZAR CONFIGURACIÓN
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "error" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cerrar
            </MDButton>
            <MDButton
              onClick={() => {
                setSubmitStatus(null);
                setStatusMessage("");
              }}
              variant="contained"
              color="warning"
              startIcon={<Refresh />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Reintentar
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "success" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <MDButton
              onClick={handleClose}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
            >
              Aceptar
            </MDButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

EditWhatsappCopDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  configuracion: PropTypes.object,
  attributes: PropTypes.object,
  loading: PropTypes.bool,
};

EditWhatsappCopDialog.defaultProps = {
  configuracion: null,
  attributes: {
    groupnoc: [],
    workflows: [],
  },
  loading: false,
};

export default React.memo(EditWhatsappCopDialog);
